import { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import axios from '@/_utils/axios';
import { selectDataTableBaseUrl } from '@/redux/selectors/dataTableSelectors';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { IngestFile, TitleEntity } from '@/redux/slices/slice_types';
import FileIngestTitleBar from './FileIngestTitlebar';
import { LabelValue, Spinner } from '@/components/_common';
import { Button, Container, Grid, Paper, Typography } from '@mui/material';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import FileTypeDisplay from '@/components/_common/FileTypeDisplay';
import styled from '@emotion/styled';
import TitleAssociation, { SelectedTitles } from './TitleAssociation';
import { useToast } from '@/components/_common/Toast';
import SchemaForm, { SchemaFormProps } from '@/components/_common/SchemaForm/SchemaForm';
import { RJSFSchema } from '@rjsf/utils';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { ClickCopy, LinkButton, PolarisIcon } from '@/exports/columnCommonComponents';
import FileIngestMediaInfo from './FileIngestMediaInfo';
import FileIngestMediaPreview from './FileIngestMediaPreview';
import History from '@/components/_common/History/History';
import Notes from '@/components/_common/Notes/Notes';
import StatusDisplay from '@/components/_common/StatusDisplay';
import TitleAssociationDisplay from './TitleAssociationDisplay';
import { addRecentTitle } from '@/redux/slices/ingestManagementSlice';
import { ConfigContext } from '@/contextAPI/configContext';
import { formatBytes } from '@/_utils/utils';
import TitleAssociationV2, { SelectedTitlesV2 } from '@/components/IngestManagement/FileIngest/TitleAssociationV2/TitleAssociationV2';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { IngestManagementConfig } from '@/__configs/config_types';

const FileIngestDetail = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const baseUrl = useAppSelector(selectDataTableBaseUrl);
  const { titleConfig, ingestManagementConfig } = useContext(ConfigContext).titleConfig?.v2 || false;
  const isTitlesV2 = titleConfig?.v2 || false;
  const disableRecentTitles = ingestManagementConfig?.disableRecentTitles;
  const [data, setData] = useState<IngestFile>({} as IngestFile);
  const [registerAgainstEntity, setRegisterAgainstEntity] = useState<Partial<TitleEntity | TitleEntityV2>>({ id: 0 });
  const [formSchema, setFormSchema] = useState<SchemaFormProps['schema'] | null>(null);
  const [isSchemaLoading, setIsSchemaLoading] = useState(false);
  const [schemaInput, setSchemaInput] = useState(data.schema_inputs || {});
  const { error, success } = useToast();
  const [registerAgainstRecent, setRegisterAgainstRecent] = useState<SelectedTitles | SelectedTitlesV2 | null>(null);
  const readOnlyTitleAssociation = useConfigValue<IngestManagementConfig['readOnlyTitleAssociation']>([
    'ingestManagementConfig',
    'readOnlyTitleAssociation'
  ]);
  const showTitleDisplay = (data.component_deliverable_type?.length ?? 0) > 0 || ['complete', 'done'].includes(data.stage);

  const { dialog: confirmationPrompt, setDialogVisible } = useConfirmationPrompt({
    title: 'Confirm Registration Abandonment',
    titleIcon: <PolarisIcon icon="cancel" height={19} style={{ opacity: 0.5, marginRight: 8 }} />,
    message: 'Are you sure you want to cancel registering this record? All unsubmitted changes will be lost.',
    onConfirm: () => {
      window.open('/ingest-management/', '_self');
    }
  });

  useEffect(() => {
    if (!registerAgainstEntity?.id) return;
    axios
      .post(`/ingest_manager/ingest_records/${id}/form/`, { entity_id: registerAgainstEntity.id })
      .then((res) => setFormSchema(res.data.data.schema))
      .catch(() => error('Error fetching version data'));
  }, [registerAgainstEntity]);

  useEffect(() => {
    dispatch(setCurrentTable('fileIngest'));
  }, []);

  useEffect(() => {
    if (data.title) {
      setRegisterAgainstEntity({ id: data.title });
    }
    if (data.schema_input) {
      setSchemaInput(data.schema_input);
    }
  }, [data]);

  useEffect(() => {
    if (!baseUrl) return;
    axios.get(`${baseUrl}${id}/`).then((res) => setData(res.data?.data));
  }, [id, baseUrl]);

  const onSubmit = ({ formData }: { formData: RJSFSchema }) => {
    setSchemaInput(formData);
    setIsSchemaLoading(true);
    axios
      .post(`/ingest_manager/ingest_records/${id}/submit/`, { schema_input: formData, title_id: registerAgainstEntity.id })
      .then(() => {
        success('Successfully ingested file.');
        setSchemaInput({});
        if (!disableRecentTitles) {
          dispatch(addRecentTitle(registerAgainstRecent));
          setRegisterAgainstRecent(null);
        }
        history.push('/ingest-management');
      })
      .catch((err) => error(err.response?.data?.message || 'Failed to ingest record.'))
      .finally(() => setIsSchemaLoading(false));
  };

  const handleClearForm = () => {
    setSchemaInput({});
    setFormSchema(null);
  };

  const handlePendingAssetClick = () => {
    dispatch(setCurrentTable('pendingAssets'));
    dispatch(clearFilters(undefined));
    dispatch(applyFilter({ type: 'filter_id', selections: [`A${data.pending_asset_id}`, 'job_asset_hid_filter'] }));
    history.push(`/pending-assets/`);
  };

  if (!data) return null;
  return (
    <div>
      <FileIngestTitleBar data={data} />

      <Container>
        <Grid container rowSpacing={3} columnSpacing={5} alignItems="flex-start" sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container alignItems="flex-start" flexWrap="nowrap">
                <FileTypeDisplay type={data.file_type} />
                <Typography variant="h6" sx={{ ml: 3 }}>
                  <ClickCopy copyText={data.file_path || data.filename}>{data.filename}</ClickCopy>
                </Typography>
              </Grid>
            </Paper>
            <FileIngestMediaPreview file={data} />
            <FileIngestMediaInfo data={data} specInput={schemaInput} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, mb: 2 }}>
              <Grid container rowSpacing={2} columnSpacing={3} alignItems="center">
                <Grid item xs={12} sm={3} lg={2}>
                  <LabelValue label="Ingest Record ID">{data.hid}</LabelValue>
                </Grid>
                {!!data.pending_asset_id && (
                  <Grid item xs={12} sm={3} lg={2}>
                    <LabelValue label="Pending Asset ID">
                      <LinkButton onClick={handlePendingAssetClick}>{data.pending_asset_id ? `A${data.pending_asset_id}` : '-'}</LinkButton>
                    </LabelValue>
                  </Grid>
                )}
                <Grid item xs={12} sm={4} lg={3}>
                  <LabelValue label="Created">
                    <DateTimeDisplay singleLine>{data.created_at}</DateTimeDisplay>
                  </LabelValue>
                </Grid>
                <Grid item xs={12} sm={3} lg={2}>
                  <LabelValue label="File Size">{formatBytes(data.file_size)}</LabelValue>
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={1} sx={{ display: 'flex', alignItems: 'center', '& .MuiButtonBase-root': { padding: '5px' } }}>
                  <LabelValue label="Notes">
                    <Notes data={data} badgeColor="primary" type="fileIngest" />
                  </LabelValue>
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <LabelValue label="History">
                    <History data={data} iconOnly />
                  </LabelValue>
                </Grid>
                <Grid item xs={12} sm={4} md={6} lg={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <StatusDisplay stage={data.stage} status={data.status} reason={data.stage_reason} />
                </Grid>
              </Grid>
            </Paper>

            {readOnlyTitleAssociation || showTitleDisplay ? (
              <Paper sx={{ p: 4 }}>
                <Typography variant="h3" sx={{ fontSize: '1.4rem', mb: 3 }}>
                  Title Association
                </Typography>
                <TitleAssociationDisplay
                  version={data.version || undefined}
                  titleS={data.title_serialized || data.version_serialized || undefined}
                  titleId={data.title || data.version_id || null}
                />
              </Paper>
            ) : data.stage === 'ready_for_ingest' ? (
              <Paper sx={{ p: 4 }}>
                <Typography variant="h3" sx={{ fontSize: '1.4rem', mb: 3 }}>
                  Title Association
                </Typography>
                {isTitlesV2 ? (
                  <TitleAssociationV2
                    onRegisterEntitySelect={(title, titles) => {
                      setRegisterAgainstEntity(title);
                      if (!disableRecentTitles) setRegisterAgainstRecent(titles);
                    }}
                    handleClearForm={handleClearForm}
                  />
                ) : (
                  <TitleAssociation
                    onRegisterEntitySelect={(title, titles) => {
                      setRegisterAgainstEntity(title);
                      if (!disableRecentTitles) setRegisterAgainstRecent(titles);
                    }}
                    handleClearForm={handleClearForm}
                  />
                )}
              </Paper>
            ) : null}
            {(isSchemaLoading || (formSchema && formSchema.properties && Object.keys(formSchema.properties).length > 0)) && (
              <Paper sx={{ p: 4, mt: 2 }}>
                {isSchemaLoading ? (
                  <Spinner style={{ position: 'relative', marginTop: 50, marginBottom: 50 }} />
                ) : (
                  <SchemaForm
                    showFormTitle
                    schema={formSchema || {}}
                    formData={schemaInput}
                    onSubmit={onSubmit}
                    readonly={data.stage !== 'ready_for_ingest'}
                  >
                    {data.stage === 'ready_for_ingest' && (
                      <ActionButtons>
                        <Button color="default" variant="text" onClick={() => setDialogVisible(true)}>
                          Cancel
                        </Button>
                        <Button type="submit" color="primary" variant="contained" disabled={!formSchema}>
                          Register
                        </Button>
                      </ActionButtons>
                    )}
                    {confirmationPrompt}
                  </SchemaForm>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default FileIngestDetail;

const ActionButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing(4)};
  column-gap: ${({ theme }) => theme.spacing(1)};
`;
